import type { CityConfig } from "../city-template/types";

export const somersetConfig: CityConfig = {
  city: "Somerset",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "somerset",
  route: "/somerset",
  title: "Emergency Passport Somerset, KY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Somerset, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Pulaski County and Lake Cumberland travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Somerset, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Somerset and Lake Cumberland travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Somerset%20Kentucky%20downtown%20Main%20Street%20at%20golden%20hour%20with%20historic%20courthouse%20and%20charming%20brick%20storefronts%2C%20beautiful%20South%20Central%20Kentucky%20landscape%2C%20Lake%20Cumberland%20area%20small%20city%20atmosphere%2C%20warm%20sunset%20light%20with%20tree-lined%20streets%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Pulaski%20County%20scene%20with%20rolling%20green%20hills%20in%20the%20distance&width=1800&height=700&seq=somerset-hero-001&orientation=landscape",
  heroImageAlt: "Somerset Kentucky downtown Main Street at golden hour with historic courthouse",
  geoPosition: "37.0920;-84.6041",
  geoPlacename: "Somerset, Kentucky",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30308",
    distance: "Located in downtown Atlanta — approximately 280 miles from Somerset via I-75",
  },
  nearbyCitiesZips: [
    { name: "Somerset", zips: "42501, 42502, 42503" },
    { name: "London", zips: "40741, 40742, 40743, 40744" },
    { name: "Corbin", zips: "40701, 40702" },
    { name: "Williamsburg", zips: "40769" },
    { name: "Monticello", zips: "42633" },
    { name: "Danville", zips: "40422, 40423" },
    { name: "Lexington", zips: "40502–40517" },
    { name: "Richmond", zips: "40475, 40476" },
    { name: "Berea", zips: "40403, 40404" },
    { name: "Nicholasville", zips: "40356, 40340" },
    { name: "Frankfort", zips: "40601, 40604" },
    { name: "Harrodsburg", zips: "40330" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Pulaski County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Lexington", route: "/lexington" },
    { name: "Fast passport renewal in Louisville", route: "/louisville" },
    { name: "Emergency passport help in Richmond", route: "/richmond" },
    { name: "Urgent passport services in London", route: "/london" },
    { name: "Same-day passport help in Corbin", route: "/corbin" },
    { name: "Passport courier services near Berea", route: "/cities/berea" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Somerset remotely via secure courier. We do not have a walk-in office in Somerset — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Pulaski County travelers.",
  fpcOfficeAddress: "500 W Jefferson St, Louisville, KY 40202",
  fpcOfficeDistance: "Serving all of Pulaski County and the Lake Cumberland region",
  serviceAreaIntro: "From downtown Somerset's historic Main Street to the Lake Cumberland shoreline, from the surrounding Pulaski County countryside to the broader South Central Kentucky region — Fast Passport Center serves the entire Somerset community. We regularly help travelers from London, Corbin, and Williamsburg get their passports fast.",
  nearbyCitiesIntro: "We serve all Somerset neighborhoods and nearby South Central Kentucky cities including London, Corbin, Williamsburg, Monticello, Danville, Lexington, Richmond, Berea, Nicholasville, Frankfort, and Harrodsburg. If you are in Pulaski County, the Lake Cumberland area, or the I-75 corridor, we have you covered.",
  howItWorksIntro: "Most Somerset travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Somerset travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Somerset and Lake Cumberland travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Somerset? Here are the most common ones we hear from Pulaski County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Pulaski County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Sandra M.",
      location: "Somerset, KY",
      rating: 5,
      text: "Booked a last-minute cruise out of Miami and realized my passport had expired. Fast Passport Center got my renewal done in 2 days. I made the ship and didn't miss a single port.",
      service: "Passport Renewal",
    },
    {
      name: "James P.",
      location: "London, KY",
      rating: 5,
      text: "Family emergency in Ireland meant I needed to leave within 48 hours. They got my emergency passport processed in 36 hours. I was on a plane to Dublin the next morning.",
      service: "Emergency Passport",
    },
    {
      name: "Karen D.",
      location: "Corbin, KY",
      rating: 5,
      text: "First-time passport for my study abroad program in Spain. The team helped me with every step and I had my passport in 4 days. Made my program start date with time to spare.",
      service: "New Passport",
    },
    {
      name: "Thomas R.",
      location: "Williamsburg, KY",
      rating: 5,
      text: "Lost my passport while packing for our family trip to Cancun. I called them in a panic and they had my replacement in 24 hours. The kids were so happy we didn't have to cancel.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "somerset-1",
      name: "Somerset Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 N Main St",
      city: "Somerset",
      state: "KY",
      zip: "42501",
      phone: "(606) 679-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Somerset",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Pulaski County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "somerset-2",
      name: "London Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 N Main St",
      city: "London",
      state: "KY",
      zip: "40741",
      phone: "(606) 864-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "35 miles from Somerset",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Laurel County and northern Pulaski County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "somerset-3",
      name: "Pulaski County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "100 N Main St",
      city: "Somerset",
      state: "KY",
      zip: "42501",
      phone: "(606) 679-2042",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Somerset Courthouse",
      website: "https://www.pulaskicounty.net/",
      websiteLabel: "Pulaski County Clerk",
      notes: "Major county facility for Pulaski County. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "somerset-4",
      name: "Corbin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 N Main St",
      city: "Corbin",
      state: "KY",
      zip: "40701",
      phone: "(606) 528-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles from Somerset",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Knox County and eastern Pulaski County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "somerset-5",
      name: "Lexington Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1055 New Circle Rd",
      city: "Lexington",
      state: "KY",
      zip: "40511",
      phone: "(859) 252-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "75 miles from Somerset",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for the Lexington area. High volume — book appointments 2+ weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "somerset-6",
      name: "Richmond Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 N Madison Ave",
      city: "Richmond",
      state: "KY",
      zip: "40475",
      phone: "(859) 623-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "65 miles from Somerset",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Madison County and western Pulaski County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 4.5 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Pulaski County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Pulaski County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Somerset applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Pulaski County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Pulaski County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Somerset." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Somerset address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Somerset before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Somerset for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 4.5 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Somerset?", a: "Fast Passport Center offers fast passport renewal in Somerset in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my Lake Cumberland vacation?", a: "If your passport expires before an international trip, contact Fast Passport Center immediately. Many Somerset travelers visit Caribbean destinations or take cruises from Florida. We can expedite your passport renewal in Somerset in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Somerset?", a: "Same-day passport appointments are only available at the Atlanta Regional Passport Agency — a 4.5 hour drive from Somerset — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Pulaski County residents." },
    { q: "Can I expedite a child passport in Somerset?", a: "Yes. Fast Passport Center expedites child passports in Somerset for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Pulaski County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Somerset with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Somerset?", a: "With Fast Passport Center's emergency courier service, Somerset travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Somerset?", a: "Popular options include the Somerset Post Office (42501), Pulaski County Clerk (42501), London Post Office (40741), Corbin Post Office (40701), Lexington Main Post Office (40511), and Richmond Post Office (40475). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Somerset?", a: "Same-day passports are only available at the Atlanta Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 4.5 hour drive to Atlanta." },
    { q: "What if I live in a smaller Pulaski County town?", a: "We serve the entire Pulaski County and surrounding area including London, Corbin, Williamsburg, Monticello, Danville, Lexington, Richmond, Berea, Nicholasville, Frankfort, and Harrodsburg. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Somerset?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Somerset?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Somerset and London offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Somerset?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Somerset?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Pulaski County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Somerset", address: "140 S Highway 27, Somerset, KY 42501", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Somerset", address: "215 S Highway 27, Somerset, KY 42501", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Somerset", address: "515 S Highway 27, Somerset, KY 42501", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Somerset", address: "100 E Columbia St, Somerset, KY 42501", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — London", address: "1750 W Highway 192, London, KY 40741", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — London", address: "1850 W Highway 192, London, KY 40741", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Corbin", address: "1000 Cumberland Falls Hwy, Corbin, KY 40701", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Lexington", address: "1650 Bryan Station Rd, Lexington, KY 40505", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Pulaski County",
      address: "100 N Main St, Somerset, KY 42501",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Laurel County",
      address: "343 S Main St, London, KY 40741",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Laurel County and northern Pulaski County residents.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Knox County",
      address: "401 Court Sq, Barbourville, KY 40906",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Knox County and eastern Pulaski County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Madison County",
      address: "101 W Main St, Richmond, KY 40475",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Madison County and western Pulaski County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SME",
      name: "Lake Cumberland Regional",
      distance: "5 miles from Somerset",
      address: "100 Aviation Blvd, Somerset, KY 42501",
      note: "The primary airport for Pulaski County. General aviation with limited commercial service.",
      flightUrl: "https://www.somersetairport.com/",
      flightLabel: "Lake Cumberland Airport",
      featured: true,
    },
    {
      code: "LEX",
      name: "Blue Grass Airport",
      distance: "75 miles from Somerset",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "Major regional airport for international flights. Accessible via Hal Rogers Parkway.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Lexington Airport",
      featured: false,
    },
    {
      code: "TYS",
      name: "McGhee Tyson Airport",
      distance: "120 miles from Somerset",
      address: "2055 Alcoa Hwy, Alcoa, TN 37701",
      note: "Alternative regional airport for international flights. Accessible via I-75.",
      flightUrl: "https://www.tys.org/",
      flightLabel: "Knoxville Airport",
      featured: false,
    },
  ],
};